import {
  applyNonLeadingSectionInputRule,
  applySectionInputRule,
  SECTION_HEADING_PATTERN,
  SECTION_NON_LEADING_HEADING_PATTERN,
  type EditorType,
} from '@heidi/editor/core';
import { InputRule } from '@tiptap/react';
import { trackSectionCreated } from './section-analytics';

/**
 * Live `# ` → section conversion for the markdown editor. Twinned with the
 * markdown-native rules of the same name: both wrappers delegate to the shared
 * transforms. See those handlers for the conversion behaviour.
 *
 * Two rules: the anchored `# ` at a paragraph start, and a non-leading rule
 * that splits at the exact inline caret (including after a soft/hard break). On
 * a successful conversion either emits `section_created` with
 * `creation_method: 'heading_shortcut'`. The rules run in ProseMirror (outside
 * React), so `editor_context` comes from the `SectionNode` storage stamped by
 * `BlockEditorMarkdown`, and the event is dispatched via the Redux `store`.
 */
export function createSectionInputRules(
  getEditorType: () => EditorType | undefined = () => undefined
): InputRule[] {
  type SectionRuleHandler = typeof applySectionInputRule;
  const withTracking =
    (handler: SectionRuleHandler): SectionRuleHandler =>
    (props) => {
      const result = handler(props);
      // `null` means the line wasn't convertible — no section was created.
      if (result === null) return result;

      trackSectionCreated(getEditorType(), 'heading_shortcut');
      return result;
    };

  return [
    new InputRule({
      find: SECTION_HEADING_PATTERN,
      handler: withTracking(applySectionInputRule),
    }),
    new InputRule({
      find: SECTION_NON_LEADING_HEADING_PATTERN,
      handler: withTracking(applyNonLeadingSectionInputRule),
    }),
  ];
}
